"use client";

import {
  type ChangeEvent,
  type FormEvent,
  useEffect,
  useRef,
  useState,
} from "react";

type FormField =
  | "firstName"
  | "lastName"
  | "workEmail"
  | "phoneNumber"
  | "companyName"
  | "roleJobTitle"
  | "country"
  | "industry"
  | "workflowType"
  | "teamSize"
  | "timeline"
  | "preferredDeliveryApproach"
  | "experience";

type FormErrors = Partial<Record<FormField, string>>;
type SubmitState = "idle" | "validating" | "submitting" | "success" | "error";

const FORM_ID = "fto_start_conversation";
const FORM_NAME = "start_the_conversation";

const industries = [
  "Insurance",
  "Healthcare",
  "Financial Services",
  "Technology",
  "Consumer Goods",
  "Education",
  "Other",
];

const workflowTypes = [
  "Customer Experience / Support",
  "Sales Development / Lead Generation",
  "Back-Office Operations",
  "Insurance Operations",
  "Healthcare Support",
  "Administrative Support",
  "Blended / Multi-Workflow",
  "Not Sure Yet",
];

const teamSizes = ["1–5", "6–15", "16–50", "51+", "Not Sure Yet"];
const timelines = [
  "Immediately",
  "Within 30 Days",
  "1–3 Months",
  "3–6 Months",
  "Exploring Options",
];

const deliveryApproaches = [
  {
    key: "AI-Enabled Delivery",
    number: "01",
    description: "Automation and AI support for repeatable, high-volume workflows.",
  },
  {
    key: "Human-Led Delivery",
    number: "02",
    description: "Dedicated teams for work requiring context, empathy, and accountability.",
  },
  {
    key: "Blended AI + Human Delivery",
    number: "03",
    description: "AI for precision and speed, with people leading critical moments and exceptions.",
  },
  {
    key: "Not Sure Yet — Help Me Design It",
    number: "04",
    description: "FTO will recommend the right balance based on your workflow and customer experience.",
  },
];

const countries = [
  "United States",
  "Canada",
  "Mexico",
  "United Kingdom",
  "Australia",
  "Argentina",
  "Brazil",
  "Chile",
  "Colombia",
  "Costa Rica",
  "Dominican Republic",
  "Ecuador",
  "El Salvador",
  "France",
  "Germany",
  "Ghana",
  "Guatemala",
  "Honduras",
  "India",
  "Ireland",
  "Italy",
  "Japan",
  "Netherlands",
  "New Zealand",
  "Nicaragua",
  "Panama",
  "Peru",
  "Philippines",
  "Portugal",
  "Puerto Rico",
  "Singapore",
  "South Africa",
  "Spain",
  "Switzerland",
  "United Arab Emirates",
  "Uruguay",
  "Other",
];

const phoneCodes = [
  { value: "+1", label: "+1 — US / Canada", min: 10, max: 10 },
  { value: "+52", label: "+52 — Mexico", min: 10, max: 10 },
  { value: "+44", label: "+44 — United Kingdom", min: 10, max: 11 },
  { value: "+61", label: "+61 — Australia", min: 9, max: 9 },
  { value: "+54", label: "+54 — Argentina", min: 10, max: 11 },
  { value: "+55", label: "+55 — Brazil", min: 10, max: 11 },
  { value: "+56", label: "+56 — Chile", min: 9, max: 9 },
  { value: "+57", label: "+57 — Colombia", min: 10, max: 10 },
  { value: "+506", label: "+506 — Costa Rica", min: 8, max: 8 },
  { value: "+1-809", label: "+1 809 — Dominican Republic", min: 10, max: 10 },
  { value: "+593", label: "+593 — Ecuador", min: 9, max: 9 },
  { value: "+503", label: "+503 — El Salvador", min: 8, max: 8 },
  { value: "+33", label: "+33 — France", min: 9, max: 9 },
  { value: "+49", label: "+49 — Germany", min: 10, max: 11 },
  { value: "+233", label: "+233 — Ghana", min: 9, max: 9 },
  { value: "+502", label: "+502 — Guatemala", min: 8, max: 8 },
  { value: "+504", label: "+504 — Honduras", min: 8, max: 8 },
  { value: "+91", label: "+91 — India", min: 10, max: 10 },
  { value: "+353", label: "+353 — Ireland", min: 9, max: 9 },
  { value: "+39", label: "+39 — Italy", min: 9, max: 11 },
  { value: "+81", label: "+81 — Japan", min: 10, max: 10 },
  { value: "+31", label: "+31 — Netherlands", min: 9, max: 9 },
  { value: "+64", label: "+64 — New Zealand", min: 8, max: 10 },
  { value: "+505", label: "+505 — Nicaragua", min: 8, max: 8 },
  { value: "+507", label: "+507 — Panama", min: 8, max: 8 },
  { value: "+51", label: "+51 — Peru", min: 9, max: 9 },
  { value: "+63", label: "+63 — Philippines", min: 10, max: 10 },
  { value: "+351", label: "+351 — Portugal", min: 9, max: 9 },
  { value: "+65", label: "+65 — Singapore", min: 8, max: 8 },
  { value: "+27", label: "+27 — South Africa", min: 9, max: 9 },
  { value: "+34", label: "+34 — Spain", min: 9, max: 9 },
  { value: "+41", label: "+41 — Switzerland", min: 9, max: 9 },
  { value: "+971", label: "+971 — United Arab Emirates", min: 9, max: 9 },
  { value: "+598", label: "+598 — Uruguay", min: 8, max: 8 },
  { value: "+", label: "Other country code", min: 7, max: 15 },
];

const countryPhoneCodes: Record<string, string> = {
  "United States": "+1",
  Canada: "+1",
  Mexico: "+52",
  "United Kingdom": "+44",
  Australia: "+61",
  Argentina: "+54",
  Brazil: "+55",
  Chile: "+56",
  Colombia: "+57",
  "Costa Rica": "+506",
  "Dominican Republic": "+1-809",
  Ecuador: "+593",
  "El Salvador": "+503",
  France: "+33",
  Germany: "+49",
  Ghana: "+233",
  Guatemala: "+502",
  Honduras: "+504",
  India: "+91",
  Ireland: "+353",
  Italy: "+39",
  Japan: "+81",
  Netherlands: "+31",
  "New Zealand": "+64",
  Nicaragua: "+505",
  Panama: "+507",
  Peru: "+51",
  Philippines: "+63",
  Portugal: "+351",
  "Puerto Rico": "+1",
  Singapore: "+65",
  "South Africa": "+27",
  Spain: "+34",
  Switzerland: "+41",
  "United Arab Emirates": "+971",
  Uruguay: "+598",
  Other: "+",
};

const attributionKeys = [
  "gclid",
  "gbraid",
  "wbraid",
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
] as const;

const textValue = (formData: FormData, name: string) =>
  String(formData.get(name) ?? "").trim();

const digitsOnly = (value: string) => value.replace(/\D/g, "");

const requiredMessages: Partial<Record<FormField, string>> = {
  firstName: "Please enter your first name.",
  lastName: "Please enter your last name.",
  workEmail: "Please enter your work email.",
  phoneNumber: "Please enter your phone number.",
  companyName: "Please enter your company name.",
  roleJobTitle: "Please enter your role or job title.",
  country: "Please select a country.",
  industry: "Please select an industry.",
  workflowType: "Please select a workflow type.",
  preferredDeliveryApproach: "Please choose a delivery approach.",
  experience: "Please tell us what experience we should improve.",
};

function validateField(field: FormField, value: string, phoneCode = "+1") {
  if (
    [
      "firstName",
      "lastName",
      "workEmail",
      "phoneNumber",
      "companyName",
      "roleJobTitle",
      "country",
      "industry",
      "workflowType",
      "preferredDeliveryApproach",
      "experience",
    ].includes(field)
    && !value.trim()
  ) {
    return requiredMessages[field] ?? "Please complete this field.";
  }

  if (
    field === "workEmail"
    && !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i.test(value)
  ) {
    return "Please enter a valid work email.";
  }

  if (field === "phoneNumber") {
    if (!/^\+\d[\d-]{0,6}$/.test(phoneCode)) {
      return "Please select a valid country code.";
    }
    const digits = digitsOnly(value);
    const config = phoneCodes.find(({ value: code }) => code === phoneCode)
      ?? phoneCodes[phoneCodes.length - 1];
    if (digits.length < config.min || digits.length > config.max) {
      return "Please enter a valid phone number, including a country code.";
    }
  }

  if (field === "experience" && value.length > 500) {
    return "Use 500 characters or fewer.";
  }

  return "";
}

function captureAttribution() {
  const attribution: Record<string, string> = {};
  if (typeof window === "undefined") return attribution;

  const params = new URLSearchParams(window.location.search);
  for (const key of attributionKeys) {
    const storageKey = `fto_attribution_${key}`;
    const current = params.get(key)?.trim();
    try {
      const stored = window.sessionStorage.getItem(storageKey)?.trim() || "";
      if (stored) {
        attribution[key] = stored;
      } else if (current) {
        window.sessionStorage.setItem(storageKey, current);
        attribution[key] = current;
      } else {
        attribution[key] = "";
      }
    } catch {
      attribution[key] = current || "";
    }
  }

  try {
    const storedLanding = window.sessionStorage.getItem("fto_attribution_landing_page");
    const storedReferrer = window.sessionStorage.getItem("fto_attribution_referrer");
    const landingPage = storedLanding || window.location.href;
    const referrer = storedReferrer || document.referrer;
    window.sessionStorage.setItem("fto_attribution_landing_page", landingPage);
    if (referrer) window.sessionStorage.setItem("fto_attribution_referrer", referrer);
    attribution.landing_page = landingPage;
    attribution.referrer = referrer;
  } catch {
    attribution.landing_page = window.location.href;
    attribution.referrer = document.referrer;
  }

  return attribution;
}

function pushAnalyticsEvent(event: Record<string, unknown>) {
  const trackingWindow = window as Window & {
    dataLayer?: Array<Record<string, unknown>>;
  };
  if (trackingWindow.dataLayer) {
    trackingWindow.dataLayer.push(event);
    return;
  }
  Object.defineProperty(trackingWindow, "dataLayer", {
    configurable: true,
    enumerable: true,
    value: [event],
    writable: true,
  });
}

function trafficSourceType(attribution: Record<string, string>) {
  if (attribution.gclid || attribution.gbraid || attribution.wbraid) return "paid_search";
  if (attribution.utm_source) return "campaign";
  if (attribution.referrer) return "referral";
  return "direct";
}

export default function DecisionPointForm() {
  const formRef = useRef<HTMLFormElement>(null);
  const formStartedAtRef = useRef(0);
  const formStartEventRef = useRef(false);
  const submissionInFlightRef = useRef(false);
  const submittedEventRef = useRef(false);
  const [errors, setErrors] = useState<FormErrors>({});
  const [submitState, setSubmitState] = useState<SubmitState>("idle");
  const [submitMessage, setSubmitMessage] = useState("");
  const [phoneCode, setPhoneCode] = useState("+1");
  const [customPhoneCode, setCustomPhoneCode] = useState("");
  const [country, setCountry] = useState("");
  const [preferredApproach, setPreferredApproach] = useState("");
  const [experienceLength, setExperienceLength] = useState(0);

  useEffect(() => {
    formStartedAtRef.current = Date.now();
    captureAttribution();
  }, []);

  const formEventParameters = (formData?: FormData) => {
    const attribution = captureAttribution();
    return {
      form_id: FORM_ID,
      form_name: FORM_NAME,
      country: formData ? textValue(formData, "country") : undefined,
      industry: formData ? textValue(formData, "industry") : undefined,
      workflow_type: formData ? textValue(formData, "workflowType") : undefined,
      delivery_approach: formData
        ? textValue(formData, "preferredDeliveryApproach")
        : undefined,
      team_size: formData ? textValue(formData, "teamSize") : undefined,
      timeline: formData ? textValue(formData, "timeline") : undefined,
      has_gclid: Boolean(attribution.gclid),
      traffic_source_type: trafficSourceType(attribution),
    };
  };

  const registerFormStart = () => {
    if (formStartEventRef.current) return;
    formStartEventRef.current = true;
    pushAnalyticsEvent({
      event: "lead_form_start",
      ...formEventParameters(),
    });
  };

  const updateFieldError = (
    field: FormField,
    value: string,
    selectedPhoneCode = phoneCode,
  ) => {
    const message = validateField(field, value, selectedPhoneCode);
    setErrors((current) => {
      if (current[field] === message) return current;
      const next = { ...current };
      if (message) next[field] = message;
      else delete next[field];
      return next;
    });
  };

  const validateForm = (formData: FormData) => {
    const nextErrors: FormErrors = {};
    const fields: FormField[] = [
      "firstName",
      "lastName",
      "workEmail",
      "phoneNumber",
      "companyName",
      "roleJobTitle",
      "country",
      "industry",
      "workflowType",
      "preferredDeliveryApproach",
      "experience",
    ];

    for (const field of fields) {
      const selectedPhoneCode = textValue(formData, "phoneCountryCode");
      const effectivePhoneCode = selectedPhoneCode === "+"
        ? textValue(formData, "customPhoneCountryCode")
        : selectedPhoneCode;
      const message = validateField(
        field,
        textValue(formData, field),
        effectivePhoneCode,
      );
      if (message) nextErrors[field] = message;
    }

    setErrors(nextErrors);
    return nextErrors;
  };

  const submitForm = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (submissionInFlightRef.current || submitState === "submitting") return;
    submissionInFlightRef.current = true;
    setSubmitState("validating");
    registerFormStart();

    const formData = new FormData(event.currentTarget);
    const nextErrors = validateForm(formData);
    if (Object.keys(nextErrors).length) {
      pushAnalyticsEvent({
        event: "lead_form_error",
        ...formEventParameters(formData),
        error_type: "validation",
        validation_error_count: Object.keys(nextErrors).length,
      });
      setSubmitMessage("Please review the highlighted fields.");
      setSubmitState("idle");
      submissionInFlightRef.current = false;
      window.requestAnimationFrame(() => {
        formRef.current
          ?.querySelector<HTMLElement>("[aria-invalid='true']")
          ?.focus();
      });
      return;
    }

    pushAnalyticsEvent({
      event: "lead_form_submit_attempt",
      ...formEventParameters(formData),
    });
    setSubmitState("submitting");
    setSubmitMessage("");

    const clientSubmissionId = crypto.randomUUID();
    const selectedPhoneCode = textValue(formData, "phoneCountryCode");
    const effectivePhoneCode = selectedPhoneCode === "+"
      ? textValue(formData, "customPhoneCountryCode")
      : selectedPhoneCode;
    const payload = {
      firstName: textValue(formData, "firstName"),
      lastName: textValue(formData, "lastName"),
      workEmail: textValue(formData, "workEmail"),
      phoneCountryCode: effectivePhoneCode,
      phoneNumber: textValue(formData, "phoneNumber"),
      companyName: textValue(formData, "companyName"),
      roleJobTitle: textValue(formData, "roleJobTitle"),
      country: textValue(formData, "country"),
      industry: textValue(formData, "industry"),
      workflowType: textValue(formData, "workflowType"),
      teamSize: textValue(formData, "teamSize"),
      timeline: textValue(formData, "timeline"),
      preferredDeliveryApproach: textValue(formData, "preferredDeliveryApproach"),
      experience: textValue(formData, "experience"),
      website: textValue(formData, "website"),
      formStartedAt: formStartedAtRef.current,
      clientSubmissionId,
      attribution: captureAttribution(),
    };

    try {
      const response = await fetch("/api/start-the-conversation", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(payload),
      });
      const result = await response.json().catch(() => null) as {
        ok?: boolean;
        submissionId?: string;
        message?: string;
      } | null;

      if (!response.ok || !result?.ok) {
        throw new Error(result?.message || "Submission failed");
      }

      const submissionId = result.submissionId || clientSubmissionId;
      let alreadyTracked = submittedEventRef.current;
      try {
        alreadyTracked ||= window.sessionStorage.getItem(
          `fto_generate_lead_${submissionId}`,
        ) === "1";
      } catch {
        // A local once-per-session guard remains active when storage is unavailable.
      }

      if (!alreadyTracked) {
        pushAnalyticsEvent({
          event: "generate_lead",
          form_id: FORM_ID,
          form_name: "start_the_conversation",
          submission_id: submissionId,
          industry: payload.industry,
          workflow_type: payload.workflowType,
          delivery_approach: payload.preferredDeliveryApproach,
          team_size: payload.teamSize,
          timeline: payload.timeline,
          country: payload.country,
          has_gclid: Boolean(payload.attribution.gclid),
          traffic_source_type: trafficSourceType(payload.attribution),
        });
        submittedEventRef.current = true;
        try {
          window.sessionStorage.setItem(`fto_generate_lead_${submissionId}`, "1");
        } catch {
          // Tracking has already been guarded in memory.
        }
      }

      setSubmitState("success");
      window.location.assign("/start-the-conversation/thank-you");
    } catch {
      pushAnalyticsEvent({
        event: "lead_form_error",
        ...formEventParameters(formData),
        error_type: "endpoint",
        validation_error_count: 0,
      });
      submissionInFlightRef.current = false;
      setSubmitState("error");
      setSubmitMessage(
        "We couldn’t submit your request. Please review your information and try again.",
      );
    }
  };

  const handleCountryChange = (event: ChangeEvent<HTMLSelectElement>) => {
    const nextCountry = event.target.value;
    setCountry(nextCountry);
    setPhoneCode(countryPhoneCodes[nextCountry] || "+");
    updateFieldError("country", nextCountry);
  };

  return (
    <form
      className="decision-form"
      ref={formRef}
      onSubmit={submitForm}
      onInputCapture={registerFormStart}
      onChangeCapture={registerFormStart}
      noValidate
      aria-describedby="decision-form-status"
      aria-busy={submitState === "validating" || submitState === "submitting"}
    >
      <div className="decision-form-section contact-information">
        <div className="decision-form-section-label">
          <span>01</span><i /><strong>Contact Information</strong>
        </div>
        <div className="decision-form-grid">
          <label className="decision-field">
            <span>First Name *</span>
            <input
              required
              name="firstName"
              autoComplete="given-name"
              maxLength={80}
              aria-invalid={Boolean(errors.firstName)}
              aria-describedby={errors.firstName ? "firstName-error" : undefined}
              onChange={(event) => {
                if (errors.firstName) updateFieldError("firstName", event.currentTarget.value);
              }}
              onBlur={(event) => updateFieldError("firstName", event.currentTarget.value)}
            />
            {errors.firstName && <small id="firstName-error" role="alert">{errors.firstName}</small>}
          </label>
          <label className="decision-field">
            <span>Last Name *</span>
            <input
              required
              name="lastName"
              autoComplete="family-name"
              maxLength={80}
              aria-invalid={Boolean(errors.lastName)}
              aria-describedby={errors.lastName ? "lastName-error" : undefined}
              onChange={(event) => {
                if (errors.lastName) updateFieldError("lastName", event.currentTarget.value);
              }}
              onBlur={(event) => updateFieldError("lastName", event.currentTarget.value)}
            />
            {errors.lastName && <small id="lastName-error" role="alert">{errors.lastName}</small>}
          </label>
          <label className="decision-field">
            <span>Work Email *</span>
            <input
              required
              type="email"
              name="workEmail"
              autoComplete="email"
              inputMode="email"
              maxLength={160}
              aria-invalid={Boolean(errors.workEmail)}
              aria-describedby={errors.workEmail ? "workEmail-error" : "workEmail-help"}
              onChange={(event) => {
                if (errors.workEmail) updateFieldError("workEmail", event.currentTarget.value);
              }}
              onBlur={(event) => updateFieldError("workEmail", event.currentTarget.value)}
            />
            {errors.workEmail
              ? <small id="workEmail-error" role="alert">{errors.workEmail}</small>
              : <small id="workEmail-help" className="field-helper">Use your company email when possible.</small>}
          </label>
          <label className="decision-field">
            <span>Phone Number *</span>
            <div className="phone-field" data-custom={phoneCode === "+" ? "true" : "false"}>
              <select
                required
                name="phoneCountryCode"
                value={phoneCode}
                aria-label="International calling code"
                onChange={(event) => {
                  setPhoneCode(event.target.value);
                  if (event.target.value !== "+") setCustomPhoneCode("");
                  const phoneInput = formRef.current?.elements.namedItem("phoneNumber");
                  if (phoneInput instanceof HTMLInputElement && phoneInput.value) {
                    const nextCode = event.target.value === "+"
                      ? customPhoneCode
                      : event.target.value;
                    updateFieldError("phoneNumber", phoneInput.value, nextCode);
                  }
                }}
              >
                {phoneCodes.map((code) => (
                  <option value={code.value} key={code.label}>{code.label}</option>
                ))}
              </select>
              {phoneCode === "+" && (
                <input
                  required
                  className="custom-phone-code"
                  name="customPhoneCountryCode"
                  value={customPhoneCode}
                  inputMode="tel"
                  autoComplete="off"
                  maxLength={6}
                  placeholder="+000"
                  aria-label="Custom international calling code"
                  onChange={(event) => {
                    const nextCode = event.currentTarget.value
                      .replace(/[^\d+]/g, "")
                      .replace(/(?!^)\+/g, "");
                    setCustomPhoneCode(nextCode);
                  }}
                />
              )}
              <input
                required
                type="tel"
                name="phoneNumber"
                autoComplete="tel"
                inputMode="tel"
                maxLength={24}
                aria-invalid={Boolean(errors.phoneNumber)}
                aria-describedby={errors.phoneNumber ? "phoneNumber-error" : undefined}
                onChange={(event) => {
                  if (errors.phoneNumber) {
                    updateFieldError(
                      "phoneNumber",
                      event.currentTarget.value,
                      phoneCode === "+" ? customPhoneCode : phoneCode,
                    );
                  }
                }}
                onBlur={(event) => updateFieldError(
                  "phoneNumber",
                  event.currentTarget.value,
                  phoneCode === "+" ? customPhoneCode : phoneCode,
                )}
              />
            </div>
            {errors.phoneNumber && <small id="phoneNumber-error" role="alert">{errors.phoneNumber}</small>}
          </label>
          <label className="decision-field">
            <span>Company Name *</span>
            <input
              required
              name="companyName"
              autoComplete="organization"
              maxLength={140}
              aria-invalid={Boolean(errors.companyName)}
              aria-describedby={errors.companyName ? "companyName-error" : undefined}
              onChange={(event) => {
                if (errors.companyName) updateFieldError("companyName", event.currentTarget.value);
              }}
              onBlur={(event) => updateFieldError("companyName", event.currentTarget.value)}
            />
            {errors.companyName && <small id="companyName-error" role="alert">{errors.companyName}</small>}
          </label>
          <label className="decision-field">
            <span>Role / Job Title *</span>
            <input
              required
              name="roleJobTitle"
              autoComplete="organization-title"
              maxLength={120}
              aria-invalid={Boolean(errors.roleJobTitle)}
              aria-describedby={errors.roleJobTitle ? "roleJobTitle-error" : undefined}
              onChange={(event) => {
                if (errors.roleJobTitle) updateFieldError("roleJobTitle", event.currentTarget.value);
              }}
              onBlur={(event) => updateFieldError("roleJobTitle", event.currentTarget.value)}
            />
            {errors.roleJobTitle && <small id="roleJobTitle-error" role="alert">{errors.roleJobTitle}</small>}
          </label>
          <label className="decision-field decision-field--full">
            <span>Country *</span>
            <select
              required
              name="country"
              autoComplete="country-name"
              value={country}
              aria-invalid={Boolean(errors.country)}
              aria-describedby={errors.country ? "country-error" : undefined}
              onChange={handleCountryChange}
            >
              <option value="">Select country</option>
              {countries.map((item) => <option value={item} key={item}>{item}</option>)}
            </select>
            {errors.country && <small id="country-error" role="alert">{errors.country}</small>}
          </label>
        </div>
      </div>

      <div className="decision-form-section delivery-needs">
        <div className="decision-form-section-label">
          <span>02</span><i /><strong>Delivery Needs</strong>
        </div>
        <div className="decision-form-grid">
          <label className="decision-field">
            <span>Industry *</span>
            <select
              required
              name="industry"
              defaultValue=""
              aria-invalid={Boolean(errors.industry)}
              aria-describedby={errors.industry ? "industry-error" : undefined}
              onBlur={(event) => updateFieldError("industry", event.currentTarget.value)}
              onChange={(event) => updateFieldError("industry", event.currentTarget.value)}
            >
              <option value="">Select industry</option>
              {industries.map((item) => <option value={item} key={item}>{item}</option>)}
            </select>
            {errors.industry && <small id="industry-error" role="alert">{errors.industry}</small>}
          </label>
          <label className="decision-field">
            <span>Workflow Type *</span>
            <select
              required
              name="workflowType"
              defaultValue=""
              aria-invalid={Boolean(errors.workflowType)}
              aria-describedby={errors.workflowType ? "workflowType-error" : undefined}
              onBlur={(event) => updateFieldError("workflowType", event.currentTarget.value)}
              onChange={(event) => updateFieldError("workflowType", event.currentTarget.value)}
            >
              <option value="">Select workflow</option>
              {workflowTypes.map((item) => <option value={item} key={item}>{item}</option>)}
            </select>
            {errors.workflowType && <small id="workflowType-error" role="alert">{errors.workflowType}</small>}
          </label>
          <label className="decision-field">
            <span>Team Size / Seats Needed <em>Optional</em></span>
            <select name="teamSize" defaultValue="">
              <option value="">Select size</option>
              {teamSizes.map((item) => <option value={item} key={item}>{item}</option>)}
            </select>
          </label>
          <label className="decision-field">
            <span>Timeline <em>Optional</em></span>
            <select name="timeline" defaultValue="">
              <option value="">Select timeline</option>
              {timelines.map((item) => <option value={item} key={item}>{item}</option>)}
            </select>
          </label>
        </div>

        <fieldset
          className="delivery-approach"
          aria-invalid={Boolean(errors.preferredDeliveryApproach)}
          aria-describedby={errors.preferredDeliveryApproach ? "preferredDeliveryApproach-error" : "delivery-approach-help"}
        >
          <legend>Preferred Delivery Approach *</legend>
          <p id="delivery-approach-help">
            Select the approach you have in mind. We can help refine the right model.
          </p>
          <div className="delivery-approach-grid">
            {deliveryApproaches.map((approach) => {
              const selected = preferredApproach === approach.key;
              return (
                <label className="delivery-approach-card" data-selected={selected ? "true" : "false"} key={approach.key}>
                  <input
                    type="radio"
                    name="preferredDeliveryApproach"
                    value={approach.key}
                    required
                    checked={selected}
                    onChange={() => {
                      setPreferredApproach(approach.key);
                      updateFieldError("preferredDeliveryApproach", approach.key);
                    }}
                  />
                  <span className="delivery-approach-index">{approach.number}</span>
                  <span className="delivery-approach-copy">
                    <strong>{approach.key}</strong>
                    <small>{approach.description}</small>
                  </span>
                  <span className="delivery-approach-state" aria-hidden="true">
                    {selected ? "Selected" : "Select"}
                  </span>
                </label>
              );
            })}
          </div>
          {errors.preferredDeliveryApproach && (
            <small id="preferredDeliveryApproach-error" className="field-error" role="alert">
              {errors.preferredDeliveryApproach}
            </small>
          )}
        </fieldset>

        <label className="decision-field experience-field">
          <span>What experience should we improve? *</span>
          <textarea
            required
            name="experience"
            rows={3}
            maxLength={500}
            placeholder="Tell us about the experience, workflow, challenge, or outcome you want to improve."
            aria-invalid={Boolean(errors.experience)}
            aria-describedby={errors.experience ? "experience-error" : "experience-count"}
            onChange={(event) => {
              setExperienceLength(event.currentTarget.value.length);
              if (errors.experience) {
                updateFieldError("experience", event.currentTarget.value);
              }
            }}
            onBlur={(event) => updateFieldError("experience", event.currentTarget.value)}
          />
          {errors.experience
            ? <small id="experience-error" role="alert">{errors.experience}</small>
            : <small id="experience-count" className="character-count">{experienceLength} / 500</small>}
        </label>
      </div>

      <label className="honeypot-field" aria-hidden="true">
        Website
        <input name="website" tabIndex={-1} autoComplete="off" />
      </label>

      <div className="decision-form-submit">
        <p>
          By submitting, you agree to our{" "}
          <span className="privacy-policy-copy">Privacy Policy</span>.
        </p>
        <button
          type="submit"
          disabled={submitState === "validating" || submitState === "submitting" || submitState === "success"}
        >
          <span>
            {submitState === "submitting"
              ? "Submitting…"
              : submitState === "success"
                ? "Request Received"
                : "Design My Delivery Model"}
          </span>
          <i aria-hidden="true">→</i>
        </button>
      </div>

      <p
        className="decision-form-status"
        id="decision-form-status"
        role={submitState === "error" || submitMessage ? "alert" : "status"}
        aria-live="polite"
      >
        {submitMessage}
      </p>
    </form>
  );
}
